import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, Trash2, ShoppingBag, ArrowRight, ChefHat, Loader2, Check, PawPrint, ArrowLeft, UserPlus } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'Treats': '🥨',
  'Chews': '🦴',
  'Doggy Ice Cream': '🍦',
  'Sample Tasting Board': '🍽️',
  'Specials': '⭐',
  'Extras': '💧',
};

const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, '').trim();

// ---------- Sub-components ----------

const Header = ({ title, subtitle, onBack }) => (
  <div className="sticky top-0 z-30 bg-[#1f6d62] text-white shadow-lg">
    <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
      {onBack && (
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          data-testid="teds-table-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      )}
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-widest opacity-70">TED&apos;S TABLE</span>
          <span className="text-[9px] uppercase tracking-widest opacity-50">by Ultimate Pet Foods</span>
        </div>
        <h1 className="text-xl font-black leading-tight">{title}</h1>
        {subtitle && <p className="text-xs text-white/80">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// ============================================================================
// Persistent promo banner — fixed to bottom of viewport on welcome + menu screens.
// Content, URL and colours are fully driven by settings.banner so the shop can
// change the promo without a code change.
// ============================================================================

const PromoBanner = ({ banner }) => {
  const navigate = useNavigate();
  if (!banner || !banner.enabled || !banner.text) return null;

  const handleClick = (e) => {
    e.preventDefault();
    let url = banner.url || '/signup';
    if (url.startsWith('/')) {
      // Tag Ted's Table as the referrer for /signup so we can redirect the
      // customer back to the menu with their welcome promo after signup.
      if (url === '/signup' || url.startsWith('/signup?') || url.startsWith('/signup#')) {
        const sep = url.includes('?') ? '&' : '?';
        url = `${url}${sep}from=teds-table`;
      }
      navigate(url);
      window.scrollTo(0, 0);
    } else {
      window.location.href = url;
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-center gap-2 px-4 py-3 text-center font-bold text-sm sm:text-base shadow-2xl hover:brightness-110 active:brightness-95 transition-all"
      style={{
        backgroundColor: banner.bg_color || '#1f6d62',
        color: banner.text_color || '#ffffff',
        paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
        borderTop: `2px solid ${banner.accent_color || '#a0da39'}`,
      }}
      data-testid="teds-table-promo-banner"
    >
      <span className="truncate max-w-[80vw]">{banner.text}</span>
      <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: banner.accent_color || '#a0da39' }} />
    </button>
  );
};

// ============================================================================
// Closed-state email capture — replaces the plain "closed" message when the
// shop is closed, letting customers leave their email so we can reopen alerts.
// ============================================================================

const ClosedEmailCapture = ({ message, clientId }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/teds-table/closed-signup`, {
        email: email.trim(),
        name: name.trim() || null,
        client_id: clientId,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your email');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bg-white/10 border border-white/20 rounded-2xl px-5 py-5 max-w-md w-full mb-4"
      data-testid="teds-table-closed-message"
    >
      <p className="text-[10px] uppercase tracking-[3px] text-red-300 font-bold mb-2">Currently Closed</p>
      <p className="text-white/90 text-base leading-relaxed mb-4">{message}</p>
      {done ? (
        <div className="bg-emerald-500/20 border border-emerald-300/30 rounded-xl px-4 py-3 text-emerald-100 text-sm text-center" data-testid="teds-table-closed-signup-done">
          <Check className="w-5 h-5 mx-auto mb-1" strokeWidth={3} />
          <p className="font-bold">Thanks! We&apos;ll email you when we reopen.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-2">
          <p className="text-white/80 text-xs mb-2">Leave your email and we&apos;ll let you know when Ted&apos;s Table reopens:</p>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            className="bg-white/15 border-white/25 text-white placeholder:text-white/50"
            data-testid="teds-table-closed-name-input"
          />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="bg-white/15 border-white/25 text-white placeholder:text-white/50"
            data-testid="teds-table-closed-email-input"
            required
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f] font-bold py-2.5 rounded-lg disabled:opacity-60 transition-colors"
            data-testid="teds-table-closed-submit"
          >
            {submitting ? 'Saving…' : 'Notify me when open'}
          </button>
        </form>
      )}
    </div>
  );
};

const WelcomeScreen = ({ onStart, isOpen, closedMessage, banner, clientId }) => (
  <div className="min-h-screen bg-gradient-to-b from-[#1f6d62] to-[#14544b] text-white flex flex-col">
    <div className="flex-1 flex flex-col items-center justify-center px-6 pb-24 text-center">
      <motion.img
        src="https://customer-assets.emergentagent.com/job_17e0cf3f-151a-4992-836a-2a69f9e5ca7d/artifacts/59irqy33_ultimate-pet-food-logo-splash-small.webp"
        alt="Ultimate Pet Foods"
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="w-64 sm:w-80 mb-6 drop-shadow-2xl"
        data-testid="teds-table-logo"
      />
      <p className="text-xs uppercase tracking-[3px] text-[#a0da39] mb-2 font-bold">TED&apos;S TABLE</p>
      <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-4">Welcome to<br/>Ted&apos;s Table</h1>

      {isOpen ? (
        <>
          <p className="text-white/85 max-w-md text-base mb-10">
            Tell Ted what your dog would like today. Fresh, hand-baked treats brought right to your table.
          </p>
          <Button
            onClick={onStart}
            className="bg-[#a0da39] hover:bg-[#8bc620] text-[#1a1a1a] px-10 py-6 rounded-2xl text-base font-bold shadow-xl"
            data-testid="teds-table-start-btn"
          >
            Start Order
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </>
      ) : (
        <ClosedEmailCapture message={closedMessage} clientId={clientId} />
      )}

      <p className="text-white/50 text-xs mt-8">The UK&apos;s First AI-Powered Dog Table Service</p>
    </div>
    <PromoBanner banner={banner} />
  </div>
);

const MenuItemCard = ({ item, quantity, onAdd, onIncrease, onDecrease }) => {
  // Backend now returns `available` bool. Fall back to legacy `visible` flag
  // for older cached payloads.
  const soldOut = item.available === false || item.visible === false;
  const stock = typeof item.stock === 'number' ? item.stock : null;
  const lowStock = !soldOut && stock !== null && stock > 0 && stock <= 5;
  const shortDesc = item.short_description || item.description;
  const hasIngredients = !!item.ingredients_html;
  const hasSuitable = Array.isArray(item.suitable_for) && item.suitable_for.length > 0;
  const hasMoreInfo = !!item.more_info_html;
  const hasAnyAccordion = hasIngredients || hasSuitable || hasMoreInfo;
  const atStockLimit = stock !== null && quantity >= stock;

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${soldOut ? 'opacity-90' : ''}`}
      data-testid={`menu-item-${item.id}`}
      data-sold-out={soldOut ? 'true' : 'false'}
    >
      <div className="flex gap-3 p-3">
        <div className="relative flex-shrink-0">
          {item.image_url && (
            <img
              src={item.image_url}
              alt={item.name}
              className={`w-20 h-20 rounded-lg object-cover ${soldOut ? 'grayscale' : ''}`}
            />
          )}
          {soldOut && (
            <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
              <span className="text-white text-[10px] font-black uppercase tracking-wider text-center leading-tight px-1">
                Sold<br/>Out
              </span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-[15px] leading-tight ${soldOut ? 'text-gray-400' : 'text-[#1a1a1a]'}`}>{item.name}</h3>
          {shortDesc && (
            <p className={`text-xs mt-1 line-clamp-2 ${soldOut ? 'text-gray-400' : 'text-gray-600'}`}>{shortDesc}</p>
          )}
          <div className="flex items-center justify-between mt-2">
            <p className={`font-black text-lg ${soldOut ? 'text-gray-400 line-through' : 'text-[#1f6d62]'}`}>
              {item.price === 0 ? 'Free' : `£${item.price.toFixed(2)}`}
            </p>
            {soldOut ? (
              <span
                className="text-[11px] font-bold uppercase tracking-wider text-red-500 border border-red-200 bg-red-50 px-3 py-1.5 rounded-full"
                data-testid={`menu-item-soldout-${item.id}`}
              >
                Sold Out
              </span>
            ) : quantity > 0 ? (
              <div className="flex items-center gap-2 bg-[#1f6d62] rounded-full p-1">
                <button
                  onClick={onDecrease}
                  className="w-7 h-7 rounded-full bg-white text-[#1f6d62] flex items-center justify-center hover:bg-gray-100"
                  data-testid={`menu-item-decrease-${item.id}`}
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="text-white font-bold text-sm min-w-[20px] text-center" data-testid={`menu-item-qty-${item.id}`}>
                  {quantity}
                </span>
                <button
                  onClick={onIncrease}
                  disabled={atStockLimit}
                  className="w-7 h-7 rounded-full bg-white text-[#1f6d62] flex items-center justify-center hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={atStockLimit ? `Only ${stock} left` : undefined}
                  data-testid={`menu-item-increase-${item.id}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {lowStock && (
                  <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full" data-testid={`menu-item-lowstock-${item.id}`}>
                    Only {stock} left
                  </span>
                )}
                <Button
                  onClick={onAdd}
                  size="sm"
                  className="bg-[#a0da39] hover:bg-[#8bc620] text-[#1a1a1a] rounded-full px-4 font-bold"
                  data-testid={`menu-item-add-${item.id}`}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {hasAnyAccordion && (
        <Accordion type="multiple" className="border-t border-gray-100 px-3">
          {hasIngredients && (
            <AccordionItem value="ingredients" className="border-b-0">
              <AccordionTrigger
                className="py-2.5 text-xs font-bold uppercase tracking-wide text-[#1f6d62] hover:no-underline"
                data-testid={`menu-item-acc-ingredients-${item.id}`}
              >
                Ingredients
              </AccordionTrigger>
              <AccordionContent className="pb-3">
                <div
                  className="text-xs text-gray-700 prose prose-sm max-w-none [&_p]:my-1 [&_ul]:pl-4 [&_ul]:my-1 [&_li]:my-0.5"
                  dangerouslySetInnerHTML={{ __html: item.ingredients_html }}
                />
              </AccordionContent>
            </AccordionItem>
          )}
          {hasSuitable && (
            <AccordionItem value="suitable" className="border-b-0">
              <AccordionTrigger
                className="py-2.5 text-xs font-bold uppercase tracking-wide text-[#1f6d62] hover:no-underline"
                data-testid={`menu-item-acc-suitable-${item.id}`}
              >
                Suitable For
              </AccordionTrigger>
              <AccordionContent className="pb-3">
                <ul className="text-xs text-gray-700 space-y-1 list-disc pl-4">
                  {item.suitable_for.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          )}
          {hasMoreInfo && (
            <AccordionItem value="more" className="border-b-0">
              <AccordionTrigger
                className="py-2.5 text-xs font-bold uppercase tracking-wide text-[#1f6d62] hover:no-underline"
                data-testid={`menu-item-acc-more-${item.id}`}
              >
                More Information
              </AccordionTrigger>
              <AccordionContent className="pb-3">
                <div
                  className="text-xs text-gray-700 prose prose-sm max-w-none [&_h4]:text-[11px] [&_h4]:font-bold [&_h4]:uppercase [&_h4]:tracking-wide [&_h4]:text-[#1a1a1a] [&_h4]:mt-3 [&_h4]:mb-1 [&_p]:my-1 [&_ul]:pl-4 [&_ul]:my-1 [&_li]:my-0.5 [&_table]:border-collapse [&_table]:my-2 [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1"
                  dangerouslySetInnerHTML={{ __html: item.more_info_html }}
                />
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      )}
    </div>
  );
};

// ============================================================================
// Welcome promo code card — shown at the top of the menu when the customer
// arrives back from /signup with ?code=NEW10 (their new-member perk).
// Purely visual: reminds the customer to mention the code at the counter
// (Stripe Checkout is un-touched for now).
// ============================================================================

const PromoCodeCard = ({ code, onDismiss }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success('Code copied!');
    } catch { /* ignore */ }
  };
  return (
    <div
      className="relative rounded-2xl border-2 border-dashed border-[#a0da39] bg-gradient-to-br from-[#1f6d62] to-[#14544b] p-4 shadow-xl"
      data-testid="teds-table-promo-code-card"
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss promo"
        className="absolute top-2 right-2 text-white/60 hover:text-white text-lg leading-none px-2"
        data-testid="teds-table-promo-code-dismiss"
      >
        ×
      </button>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-2xl">🎉</span>
        <p className="text-[10px] uppercase tracking-[3px] text-[#a0da39] font-bold">Welcome to Ted&apos;s Table!</p>
      </div>
      <p className="text-white text-sm mb-3">
        Your account is ready — your <strong>10% discount</strong> will be applied automatically at checkout.
      </p>
      <button
        type="button"
        onClick={copy}
        className="w-full flex items-center justify-between gap-3 bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f] font-black rounded-xl px-4 py-3 text-lg tracking-widest uppercase transition-colors"
        data-testid="teds-table-promo-code-value"
      >
        <span className="flex-1 text-center">{code}</span>
        <span className="text-xs font-bold uppercase tracking-widest bg-white/60 rounded-md px-2 py-1">
          {copied ? 'Copied' : 'Tap to copy'}
        </span>
      </button>
    </div>
  );
};

const MenuScreen = ({ menu, cart, onAdd, onIncrease, onDecrease, onCheckout, onBack, tableFromQr, banner, promoCode, onDismissPromo, promo }) => {
  const totalQty = Object.values(cart).reduce((s, q) => s + q, 0);
  const subtotal = menu.flatMap(g => g.items).reduce((s, it) => s + (it.price * (cart[it.id] || 0)), 0);
  // Pence-precision math to stay perfectly in sync with the backend (which
  // charges the customer). Discount is round-half-up on pence.
  const subtotalPence = Math.round(subtotal * 100);
  const discountPence = promo ? Math.floor((subtotalPence * promo.percent_off + 50) / 100) : 0;
  const discount = discountPence / 100;
  const total = Math.max(subtotalPence - discountPence, 0) / 100;
  // Extra bottom padding when the persistent promo banner is active, so the
  // cart pill and the last menu item don't collide with it.
  const hasBanner = banner && banner.enabled && banner.text;
  const listBottomPadding = totalQty > 0 ? (hasBanner ? 'pb-40' : 'pb-24') : (hasBanner ? 'pb-24' : 'pb-8');

  return (
    <div className={`min-h-screen bg-gray-50 ${listBottomPadding}`}>
      <Header
        title="Menu"
        subtitle={tableFromQr ? `Table ${tableFromQr}` : 'Choose treats for your dog'}
        onBack={onBack}
      />
      <div className="max-w-3xl mx-auto px-4 py-4 space-y-6">
        {promoCode && (
          <PromoCodeCard code={promoCode} onDismiss={onDismissPromo} />
        )}
        {menu.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Menu is being prepared. Please try again in a moment.</p>
          </div>
        ) : menu.map(group => (
          <div key={group.category}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{CATEGORY_ICONS[group.category] || '🐾'}</span>
              <h2 className="text-lg font-black text-[#1a1a1a] uppercase tracking-wide">{group.category}</h2>
            </div>
            <div className="space-y-2">
              {group.items.map(item => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  quantity={cart[item.id] || 0}
                  onAdd={() => onAdd(item.id)}
                  onIncrease={() => onIncrease(item.id)}
                  onDecrease={() => onDecrease(item.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {totalQty > 0 && (
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#a0da39] shadow-2xl z-[60]"
          >
            <div className="max-w-3xl mx-auto px-4 py-3 pr-24 sm:pr-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">
                  {totalQty} {totalQty === 1 ? 'item' : 'items'}
                  {promo && (
                    <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#a0da39] bg-[#1f6d62] rounded-full px-2 py-0.5" data-testid="cart-promo-badge">
                      {promo.code} · -{promo.percent_off}%
                    </span>
                  )}
                </p>
                {discount > 0 ? (
                  <p className="text-2xl font-black text-[#1f6d62]" data-testid="cart-total">
                    £{total.toFixed(2)}{' '}
                    <span className="text-sm font-semibold text-gray-400 line-through ml-1">£{subtotal.toFixed(2)}</span>
                  </p>
                ) : (
                  <p className="text-2xl font-black text-[#1f6d62]" data-testid="cart-total">£{total.toFixed(2)}</p>
                )}
              </div>
              <Button
                onClick={onCheckout}
                className="bg-[#1f6d62] hover:bg-[#14544b] text-white px-6 py-6 rounded-xl font-bold flex-1 max-w-[220px]"
                data-testid="teds-table-review-btn"
              >
                Review Order
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Promo banner — only when cart is empty (avoids cart pill collision) */}
      {totalQty === 0 && <PromoBanner banner={banner} />}
    </div>
  );
};

const TableDetailsScreen = ({ menu, cart, onAdd, onIncrease, onDecrease, onSubmit, onBack, submitting, tableFromQr, promo, onRemovePromo }) => {
  const [tableNumber, setTableNumber] = useState(tableFromQr || '');
  const [dogName, setDogName] = useState('');
  const [ownerName, setOwnerName] = useState('');

  const cartItems = menu.flatMap(g => g.items).filter(it => (cart[it.id] || 0) > 0);
  const subtotal = cartItems.reduce((s, it) => s + (it.price * cart[it.id]), 0);
  const subtotalPence = Math.round(subtotal * 100);
  const discountPence = promo ? Math.floor((subtotalPence * promo.percent_off + 50) / 100) : 0;
  const discount = discountPence / 100;
  const total = Math.max(subtotalPence - discountPence, 0) / 100;

  const handleSubmit = () => {
    if (!tableNumber.trim()) {
      toast.error('Please enter your table number');
      return;
    }
    if (!dogName.trim()) {
      toast.error("Please tell us your dog's name");
      return;
    }
    onSubmit({ tableNumber: tableNumber.trim(), dogName: dogName.trim(), ownerName: ownerName.trim() });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-40">
      <Header title="Review & Pay" onBack={onBack} />
      <div className="max-w-3xl mx-auto px-4 py-5 space-y-5">
        {/* Order Summary */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">Your Order</h2>
          <div className="space-y-3">
            {cartItems.map(it => (
              <div key={it.id} className="flex items-center gap-3">
                {it.image_url && <img src={it.image_url} alt={it.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#1a1a1a] text-sm leading-tight truncate">{it.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {it.price === 0 ? 'Free' : `£${it.price.toFixed(2)}`}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 bg-gray-100 rounded-full p-1">
                  <button onClick={() => onDecrease(it.id)} className="w-6 h-6 rounded-full bg-white flex items-center justify-center" data-testid={`review-decrease-${it.id}`}>
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-sm font-bold min-w-[16px] text-center">{cart[it.id]}</span>
                  <button onClick={() => onIncrease(it.id)} className="w-6 h-6 rounded-full bg-white flex items-center justify-center" data-testid={`review-increase-${it.id}`}>
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
                <p className="font-bold text-[#1f6d62] text-sm min-w-[56px] text-right">£{(it.price * cart[it.id]).toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200 mt-4 pt-3 space-y-1.5">
            {promo && discount > 0 && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <p className="text-gray-500">Subtotal</p>
                  <p className="text-gray-700" data-testid="review-subtotal">£{subtotal.toFixed(2)}</p>
                </div>
                <div className="flex items-center justify-between text-sm" data-testid="review-discount-row">
                  <p className="text-[#0FA49C] font-semibold flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#1f3d2f] bg-[#a0da39] rounded-full px-2 py-0.5" data-testid="review-promo-code">
                      {promo.code}
                    </span>
                    <span>-{promo.percent_off}% off</span>
                    <button
                      type="button"
                      onClick={onRemovePromo}
                      className="text-xs text-gray-400 hover:text-red-500 underline"
                      data-testid="review-remove-promo"
                    >
                      remove
                    </button>
                  </p>
                  <p className="text-[#0FA49C] font-semibold" data-testid="review-discount-amount">-£{discount.toFixed(2)}</p>
                </div>
              </>
            )}
            <div className="flex items-center justify-between pt-1">
              <p className="font-bold text-[#1a1a1a]">Total</p>
              <p className="text-2xl font-black text-[#1f6d62]" data-testid="review-total">£{total.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Table details form */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500">Table Details</h2>
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Table Number*</label>
            <Input
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="e.g. 5"
              className="text-lg"
              data-testid="teds-table-number-input"
              disabled={!!tableFromQr}
            />
            {tableFromQr && <p className="text-xs text-gray-500 mt-1">Auto-detected from QR code</p>}
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Dog&apos;s Name*</label>
            <Input
              value={dogName}
              onChange={(e) => setDogName(e.target.value)}
              placeholder="e.g. Ted"
              data-testid="teds-table-dog-name-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Your First Name <span className="text-gray-400 font-normal">(optional)</span></label>
            <Input
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="e.g. Sarah"
              data-testid="teds-table-owner-name-input"
            />
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#a0da39] shadow-2xl z-[60]">
        <div className="max-w-3xl mx-auto px-4 py-3 pr-24 sm:pr-4">
          <Button
            onClick={handleSubmit}
            disabled={submitting || cartItems.length === 0}
            className="w-full bg-[#1f6d62] hover:bg-[#14544b] text-white py-6 rounded-xl font-bold text-base disabled:opacity-60"
            data-testid="teds-table-pay-btn"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Setting up payment…</>
            ) : total <= 0 ? (
              <>Place Order <ArrowRight className="w-5 h-5 ml-2" /></>
            ) : (
              <>Pay £{total.toFixed(2)} <ArrowRight className="w-5 h-5 ml-2" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------- Main Page ----------

export const TedsTablePage = () => {
  useDocumentTitle(
    "Ted's Table — In-store Ordering | Ultimate Pet Foods",
    "Order fresh, hand-baked treats for your dog from Ted's Table at Ffos Caerffili. Scan the QR code at your table and order."
  );
  const [searchParams] = useSearchParams();
  const tableFromQr = searchParams.get('table') || '';
  // Welcome promo code from post-signup redirect (`?code=NEW10`). Also
  // persisted in sessionStorage so it survives across welcome/menu/review
  // navigation and page refreshes within the same table session.
  const [promoCode, setPromoCode] = useState(() =>
    (searchParams.get('code') || sessionStorage.getItem('teds_table_promo') || '').toUpperCase()
  );
  const [promo, setPromo] = useState(null); // {code, percent_off, description} | null
  useEffect(() => {
    if (promoCode) sessionStorage.setItem('teds_table_promo', promoCode);
    else sessionStorage.removeItem('teds_table_promo');
  }, [promoCode]);
  // Validate the code with the server whenever it changes. If invalid, drop it.
  useEffect(() => {
    if (!promoCode) { setPromo(null); return; }
    let cancelled = false;
    axios.post(`${API}/teds-table/validate-promo`, { code: promoCode })
      .then(res => {
        if (cancelled) return;
        if (res.data?.valid) setPromo(res.data);
        else {
          setPromo(null);
          setPromoCode('');
        }
      })
      .catch(() => { if (!cancelled) setPromo(null); });
    return () => { cancelled = true; };
  }, [promoCode]);

  const [step, setStep] = useState(() => (searchParams.get('code') ? 'menu' : 'welcome')); // welcome | menu | review
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({}); // { itemId: qty }
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({ is_open: false, closed_message: '' });

  // Clean the ?code=... query param out of the URL once we've captured it,
  // so a page reload doesn't re-show the "welcome" state and so the URL
  // stays clean for sharing.
  useEffect(() => {
    if (searchParams.get('code')) {
      const params = new URLSearchParams(searchParams);
      params.delete('code');
      const qs = params.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/teds-table/menu`).then(res => setMenu(res.data || [])),
      axios.get(`${API}/teds-table/settings`).then(res => setSettings(res.data || { is_open: false, closed_message: '' })),
    ])
      .catch(() => toast.error('Could not load menu'))
      .finally(() => setLoading(false));
  }, []);

  // Heartbeat presence for the admin counter — every 15s while page is open.
  // Also fires a funnel `track` event on the very first entry into each step
  // (deduped server-side by (client_id, event_type, day)).
  useEffect(() => {
    let clientId = sessionStorage.getItem('teds_table_client_id');
    if (!clientId) {
      clientId = 'tt_' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      sessionStorage.setItem('teds_table_client_id', clientId);
    }
    const ping = () => {
      axios.post(`${API}/teds-table/heartbeat`, { client_id: clientId, step }).catch(() => {});
    };
    ping();
    // Fire funnel track event for this step
    const eventMap = { welcome: 'welcome_open', menu: 'menu_open', review: 'review_open' };
    if (eventMap[step]) {
      axios.post(`${API}/teds-table/track`, { client_id: clientId, event_type: eventMap[step] }).catch(() => {});
    }
    // Extra scan_while_closed event when shop is closed at welcome time —
    // powers the admin "how many people scanned while closed" counter.
    if (step === 'welcome' && !settings.is_open) {
      axios.post(`${API}/teds-table/track`, { client_id: clientId, event_type: 'scan_while_closed' }).catch(() => {});
    }
    const timer = setInterval(ping, 15000);
    return () => clearInterval(timer);
  }, [step, settings.is_open]);

  const clientId = typeof window !== 'undefined' ? sessionStorage.getItem('teds_table_client_id') : null;

  const addToCart = (itemId) => setCart(prev => ({ ...prev, [itemId]: (prev[itemId] || 0) + 1 }));
  const increase = (itemId) => setCart(prev => ({ ...prev, [itemId]: (prev[itemId] || 0) + 1 }));
  const decrease = (itemId) => setCart(prev => {
    const cur = prev[itemId] || 0;
    if (cur <= 1) {
      const { [itemId]: _removed, ...rest } = prev;
      return rest;
    }
    return { ...prev, [itemId]: cur - 1 };
  });

  const handleCheckout = async ({ tableNumber, dogName, ownerName }) => {
    setSubmitting(true);
    try {
      const items = Object.entries(cart).map(([item_id, quantity]) => ({ item_id, quantity }));
      const res = await axios.post(`${API}/teds-table/checkout`, {
        table_number: tableNumber,
        dog_name: dogName,
        owner_first_name: ownerName || null,
        items,
        origin_url: window.location.origin,
        promo_code: promoCode || null,
      });
      if (res.data.url) {
        // Clear the promo (single use per session — either the free-order
        // path already applied it, or Stripe is about to).
        sessionStorage.removeItem('teds_table_promo');
        window.location.href = res.data.url;
      } else {
        toast.error('Could not start checkout');
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1f6d62] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-center" richColors />
      {step === 'welcome' && (
        <WelcomeScreen
          onStart={() => setStep('menu')}
          isOpen={!!settings.is_open}
          closedMessage={settings.closed_message}
          banner={settings.banner}
          clientId={clientId}
        />
      )}
      {step === 'menu' && (
        <MenuScreen
          menu={menu}
          cart={cart}
          onAdd={addToCart}
          onIncrease={increase}
          onDecrease={decrease}
          onCheckout={() => setStep('review')}
          onBack={() => setStep('welcome')}
          tableFromQr={tableFromQr}
          banner={settings.banner}
          promoCode={promoCode}
          onDismissPromo={() => setPromoCode('')}
          promo={promo}
        />
      )}
      {step === 'review' && (
        <TableDetailsScreen
          menu={menu}
          cart={cart}
          onAdd={addToCart}
          onIncrease={increase}
          onDecrease={decrease}
          onSubmit={handleCheckout}
          onBack={() => setStep('menu')}
          submitting={submitting}
          tableFromQr={tableFromQr}
          promo={promo}
          onRemovePromo={() => setPromoCode('')}
        />
      )}
    </>
  );
};

// ---------- Success page ----------

// ============================================================================
// Post-order account signup — shown on the confirmation screen for guests only.
// Prefills name + email from what we already captured (Stripe / order form) so
// the customer only needs to add a password to create their account and
// unlock order history, saved dogs and price alerts.
// ============================================================================

const PostOrderSignup = ({ order }) => {
  const { customer, register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (order?.customer_name) setName(order.customer_name);
    else if (order?.owner_first_name) setName(order.owner_first_name);
    if (order?.customer_email) setEmail(order.customer_email);
  }, [order]);

  // Order matters: once submission succeeds we flip `done` AND register() sets
  // the auth context's `customer`. Check `done` BEFORE `customer` so the
  // confirmation UI actually renders after the account is created.
  if (done) {
    return (
      <div
        className="mt-5 bg-emerald-500/20 border border-emerald-300/30 rounded-2xl p-4 text-center text-white"
        data-testid="post-order-signup-done"
      >
        <Check className="w-6 h-6 mx-auto mb-1 text-[#a0da39]" strokeWidth={3} />
        <p className="font-bold">Account created!</p>
        <p className="text-xs text-white/80 mt-1">You&apos;re now signed in — head to My Account to see this order.</p>
      </div>
    );
  }

  if (customer) return null; // already logged in on arrival — hide the form

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      await register(name.trim(), email.trim(), password, 'teds_table');
      setDone(true);
      toast.success('Account created — welcome!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mt-5 bg-white/10 border border-white/20 rounded-2xl p-4"
      data-testid="post-order-signup"
    >
      <div className="flex items-center gap-2 mb-2">
        <UserPlus className="w-5 h-5 text-[#a0da39]" />
        <h3 className="font-bold text-white text-base">Save this order — create an account</h3>
      </div>
      <p className="text-white/80 text-xs mb-3">
        Track future orders, save your dog&apos;s profile and get treat-day reminders. Takes 10 seconds.
      </p>
      <form onSubmit={submit} className="space-y-2">
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="bg-white/15 border-white/25 text-white placeholder:text-white/50"
          data-testid="post-order-signup-name"
          required
        />
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="bg-white/15 border-white/25 text-white placeholder:text-white/50"
          data-testid="post-order-signup-email"
          required
        />
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Choose a password"
          className="bg-white/15 border-white/25 text-white placeholder:text-white/50"
          data-testid="post-order-signup-password"
          required
        />
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f] font-bold py-2.5 rounded-lg disabled:opacity-60 transition-colors"
          data-testid="post-order-signup-submit"
        >
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </div>
  );
};

export const TedsTableSuccess = () => {
  useDocumentTitle(
    "Order Confirmed — Ted's Table | Ultimate Pet Foods",
    "Your Ted's Table order is confirmed and on its way to your table."
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('checking'); // checking | paid | pending | failed
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/teds-table/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setOrder(res.data);
          setStatus('paid');
          // Funnel: log a 'paid' event tied to this client's session so the
          // admin dashboard can compute a scan → pay conversion rate.
          try {
            const clientId = sessionStorage.getItem('teds_table_client_id');
            if (clientId) {
              axios.post(`${API}/teds-table/track`, { client_id: clientId, event_type: 'paid' }).catch(() => {});
            }
          } catch { /* ignore */ }
          return;
        }
        if (res.data.payment_status === 'cancelled' || res.data.payment_status === 'failed') {
          setStatus('failed');
          return;
        }
        if (n >= 8) {
          setStatus('pending');
          return;
        }
        setAttempts(n);
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        if (n >= 8) {
          setStatus('failed');
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  if (status === 'checking' || status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f6d62] to-[#14544b] flex items-center justify-center px-6 text-white text-center">
        <div>
          <Loader2 className="w-12 h-12 mx-auto animate-spin mb-4" />
          <p className="text-lg font-bold mb-1">Confirming your order…</p>
          <p className="text-white/70 text-sm">Just a moment (attempt {attempts + 1})</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-2xl font-black text-red-600 mb-2">Payment not completed</p>
          <p className="text-gray-600 mb-6">Your order was not charged. Please try again.</p>
          <Button
            onClick={() => navigate('/teds-table')}
            className="bg-[#1f6d62] hover:bg-[#14544b] text-white px-6 py-6 rounded-xl"
            data-testid="teds-table-retry-btn"
          >
            Back to Menu
          </Button>
        </div>
      </div>
    );
  }

  // status === 'paid'
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f6d62] to-[#14544b] text-white px-4 py-6">
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.6 }}
          className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#a0da39] flex items-center justify-center shadow-2xl"
        >
          <Check className="w-10 h-10 text-[#1f6d62]" strokeWidth={4} />
        </motion.div>
        <h1 className="text-3xl font-black text-center mb-1">Thanks!</h1>
        <p className="text-center text-white/85 mb-6">
          Ted has received your order. We&apos;ll bring it to your table shortly.
        </p>

        <div className="bg-white text-[#1a1a1a] rounded-2xl p-5 shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500">Order</p>
              <p className="font-black text-lg" data-testid="success-order-number">{order?.order_number}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-gray-500">Table</p>
              <p className="font-black text-lg">{order?.table_number}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <PawPrint className="w-4 h-4 text-[#1f6d62]" />
            <p className="text-sm text-gray-700">Order for <strong>{order?.dog_name}</strong></p>
          </div>

          <div className="space-y-1.5 mb-3">
            {order?.items?.map((it, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{it.name} ×{it.quantity}</span>
                <span className="font-semibold text-[#1f6d62]">£{it.line_total.toFixed(2)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 pt-3 space-y-1.5">
            {order?.promo_code && order?.discount_amount > 0 && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-700" data-testid="success-subtotal">£{order.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-sm" data-testid="success-discount-row">
                  <span className="text-[#0FA49C] font-semibold flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#1f3d2f] bg-[#a0da39] rounded-full px-2 py-0.5">
                      {order.promo_code}
                    </span>
                    <span>-{order.discount_percent}% off</span>
                  </span>
                  <span className="text-[#0FA49C] font-semibold" data-testid="success-discount-amount">-£{order.discount_amount.toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between pt-1">
              <p className="font-bold">Total paid</p>
              <p className="text-2xl font-black text-[#1f6d62]" data-testid="success-total">£{order?.total?.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <PostOrderSignup order={order} />

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/teds-table')}
            className="text-white/70 hover:text-white text-sm underline"
            data-testid="success-new-order-btn"
          >
            Place another order
          </button>
        </div>
      </div>
    </div>
  );
};

export default TedsTablePage;
